import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import ModalMotion, { AnimateDirection } from "@/components/Molecules/Modal";
import Button from "@/components/Atoms/Button";

import FormInputs from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/FormInputs";

import { useAppDispatch, useAppSelector } from "@/hooks";
import { DEFAULT_URL_ARGUMENTS } from "@/constants";

import * as actions from "@/redux/ShyftPassReducer";

import formStyles from "@/containers/ShyftPassPage/RegisterFormSection/RegistrationForm/FormInputs.module.scss";
import styles from "./WaitListModal.module.scss";

import { getURLParams, timezone } from "@/utils";

export const formSchema = yup
  .object({
    name: yup
      .string()
      .required("Please enter your name")
      .min(3, "Please enter full name"),
    mobile: yup
      .string()
      .required("Please enter a valid phone number")
      .min(8, "Number should be minimum 8 characters")
      .max(14, "Number should be maximum 14 characters"),
    email: yup
      .string()
      .email("Please enter a valid email address")
      .required("Please enter a valid email address"),
  })
  .required();

const WaitListModal = ({ openWaitListModal, setOpenWaitListModal }) => {
  const dispatch = useAppDispatch();

  const { title, centerTiming, encodedId, onboardId } = openWaitListModal;

  let urlParams = "";
  if (typeof window !== "undefined") {
    urlParams = window.location.search;
  }

  const urlsArguments = urlParams || DEFAULT_URL_ARGUMENTS;

  const formData = {
    showNameField: true,
    namePlaceholder: "Enter your name",
    showPhoneField: true,
    phonePlaceholder: "Phone Number",
    showEmailField: true,
    emailPlaceholder: "Enter E-mail address",
  };

  const [isLoading, setIsLoading] = useState(false);

  const defaultValues = useMemo(
    () => ({
      name: "",
      mobileCountryCode: "+91",
      mobile: "",
      email: "",
    }),
    []
  );

  const resolver = useMemo(() => yupResolver(formSchema), []);

  const methods = useForm({
    resolver,
    defaultValues,
    mode: "onBlur",
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = methods;

  const setDefaultValuesFromParams = useCallback(() => {
    const params = getURLParams();
    const name = params?.get("name") || "";
    const mobile = params?.get("phone") || "";
    const email = params?.get("email") || "";

    if (name || mobile || email) {
      reset((formValues) => ({
        ...formValues,
        name,
        mobile,
        email,
      }));
    }
  }, [reset]);

  useEffect(() => {
    setDefaultValuesFromParams();
  }, [setDefaultValuesFromParams]);

  const mapData = useCallback(() => {
    const { name, mobileCountryCode, mobile, email } = getValues();

    return {
      ...(name && { name: name }),
      ...(mobile &&
        mobileCountryCode && {
          primary_contact: `${mobileCountryCode}${mobile}`,
        }),
      ...(email && { email: email }),
      ...(centerTiming && { center_timing: centerTiming }),
      ...(encodedId && { encoded_id: encodedId }),
      ...(onboardId && { onboard_id: onboardId }),
      ...(urlsArguments && { urls_arguments: urlsArguments }),
      timezone,
    };
  }, [getValues, urlsArguments, centerTiming, encodedId, onboardId]);

  const onSubmit = () => {
    if (Object.keys(errors)?.length === 0) {
      setIsLoading(true);
      const apiData = mapData();

      dispatch(actions.addToWaitList({ data: apiData })).then(() => {
        setIsLoading(false);
        setOpenWaitListModal(false);
      });
    }
  };

  const purchasePlanButton = (
    <Button isLoading={isLoading} className={styles.button} type="submit">
      <span>Submit details</span>
    </Button>
  );

  return (
    <ModalMotion
      className={styles.detailModal}
      overlayClosable={true}
      onClose={() => setOpenWaitListModal(false)}
      width="500px"
      animateDirection={AnimateDirection.CENTER}
      borderRadius="10px"
      showClose
      iconTopPosition="20px"
      iconRightPosition="20px"
      // btnPos={btnPos}
      bodyPadding={"40px"}
    >
      <div className={styles.waitListModal}>
        <div
          className={[formStyles.registerForm, "form"].join(" ")}
          id="add-to-wait-list"
        >
          <div className={[formStyles.formHeading, "heading"].join(" ")}>
            <h3 className={[formStyles.title, "title"].join(" ")}>
              Join the waitlist
            </h3>
            <p
              className={[formStyles.description, "description"].join(" ")}
              dangerouslySetInnerHTML={{
                __html: `Please fill in the details to join the waitlist for - <strong>${title}</strong>`,
              }}
            />
          </div>
          <div className={formStyles.formInputs}>
            <FormProvider {...methods}>
              <FormInputs
                isLoading={isLoading}
                control={control}
                handleSubmit={handleSubmit}
                onSubmitCallback={onSubmit}
                purchasePlanButton={purchasePlanButton}
                {...formData}
              />
            </FormProvider>
          </div>
        </div>
      </div>
    </ModalMotion>
  );
};

export default WaitListModal;
